import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/api';
import toast from 'react-hot-toast';

export const createBooking = createAsyncThunk('booking/create', async (data, { rejectWithValue }) => {
  try {
    const res = await api.post('/bookings', data);
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Booking failed');
  }
});

export const fetchMyBookings = createAsyncThunk('booking/fetchMine', async (params, { rejectWithValue }) => {
  try {
    const query = new URLSearchParams(params || {}).toString();
    const res = await api.get(`/bookings/my?${query}`);
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch bookings');
  }
});

export const fetchBookingById = createAsyncThunk('booking/fetchById', async (id, { rejectWithValue }) => {
  try {
    const res = await api.get(`/bookings/${id}`);
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch booking');
  }
});

export const cancelBooking = createAsyncThunk('booking/cancel', async ({ id, reason }, { rejectWithValue }) => {
  try {
    const res = await api.put(`/bookings/${id}/cancel`, { reason });
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Cancellation failed');
  }
});

export const extendBooking = createAsyncThunk('booking/extend', async ({ id, hours }, { rejectWithValue }) => {
  try {
    const res = await api.put(`/bookings/${id}/extend`, { additionalHours: hours });
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Extension failed');
  }
});

export const endParking = createAsyncThunk('booking/end', async (id, { rejectWithValue }) => {
  try {
    const res = await api.put(`/bookings/${id}/end`);
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Failed to end parking');
  }
});

export const createPaymentOrder = createAsyncThunk('booking/createOrder', async (data, { rejectWithValue }) => {
  try {
    const res = await api.post('/payments/create-order', data);
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Payment order failed');
  }
});

export const verifyPayment = createAsyncThunk('booking/verifyPayment', async (data, { rejectWithValue }) => {
  try {
    const res = await api.post('/payments/verify', data);
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Payment verification failed');
  }
});

const bookingSlice = createSlice({
  name: 'booking',
  initialState: {
    bookings: [],
    active: null,
    selected: null,
    paymentOrder: null,
    loading: false,
    error: null,
    total: 0,
  },
  reducers: {
    clearSelected: (state) => { state.selected = null; },
    clearPaymentOrder: (state) => { state.paymentOrder = null; },
    clearError: (state) => { state.error = null; },
    setActive: (state, action) => { state.active = action.payload; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createBooking.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(createBooking.fulfilled, (state, action) => {
        state.loading = false;
        state.selected = action.payload.data;
        toast.success('Booking created! Complete payment to confirm.');
      })
      .addCase(createBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        toast.error(action.payload);
      })
      .addCase(fetchMyBookings.pending, (state) => { state.loading = true; })
      .addCase(fetchMyBookings.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings = action.payload.data || [];
        state.total = action.payload.count || 0;
        const active = (action.payload.data || []).find(b => b.status === 'active' || b.status === 'confirmed');
        if (active) state.active = active;
      })
      .addCase(fetchMyBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchBookingById.fulfilled, (state, action) => {
        state.selected = action.payload.data;
      })
      .addCase(cancelBooking.fulfilled, (state, action) => {
        const idx = state.bookings.findIndex(b => b._id === action.payload.data._id);
        if (idx !== -1) state.bookings[idx] = action.payload.data;
        if (state.active?._id === action.payload.data._id) state.active = null;
        toast.success('Booking cancelled. Refund will be processed.');
      })
      .addCase(cancelBooking.rejected, (state, action) => {
        toast.error(action.payload);
      })
      .addCase(extendBooking.fulfilled, (state, action) => {
        const idx = state.bookings.findIndex(b => b._id === action.payload.data._id);
        if (idx !== -1) state.bookings[idx] = action.payload.data;
        if (state.active?._id === action.payload.data._id) state.active = action.payload.data;
        toast.success('Booking extended successfully!');
      })
      .addCase(extendBooking.rejected, (state, action) => {
        toast.error(action.payload);
      })
      .addCase(endParking.fulfilled, (state, action) => {
        const idx = state.bookings.findIndex(b => b._id === action.payload.data._id);
        if (idx !== -1) state.bookings[idx] = action.payload.data;
        state.active = null;
        toast.success('Parking ended. Thank you!');
      })
      .addCase(endParking.rejected, (state, action) => {
        toast.error(action.payload);
      })
      .addCase(createPaymentOrder.fulfilled, (state, action) => {
        state.paymentOrder = action.payload.data;
      })
      .addCase(verifyPayment.fulfilled, (state, action) => {
        state.selected = action.payload.data;
        toast.success('Payment successful! Booking confirmed.');
      })
      .addCase(verifyPayment.rejected, (state, action) => {
        toast.error(action.payload);
      });
  },
});

export const { clearSelected, clearPaymentOrder, clearError, setActive } = bookingSlice.actions;
export default bookingSlice.reducer;